import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { getDdpCarriersForCountry, getDdpWalletedCarriersForCountry } from '../config/ddp-country-config';
import Global from '../store/global-selectors';

/**
 * Build a Set of sellerLabelProviderIds for providers matching the given carrierIds
 * @param {object[]} labelProviders - Array of label providers from Redux
 * @param {string[]} carrierIds - Array of carrier IDs to match against
 * @returns {Set<string|number>}
 */
const buildSellerProviderIdSet = (labelProviders, carrierIds) => {
  const providerIdSet = new Set();

  if (labelProviders?.length > 0 && carrierIds.length > 0) {
    labelProviders.forEach((provider) => {
      if (carrierIds.includes(provider.labelProviderId)) {
        providerIdSet.add(provider.sellerLabelProviderId);
      }
    });
  }

  return providerIdSet;
};

/**
 * Hook to check DDP-eligible carriers for seller's country
 * @returns {{
 *   hasDdpEligibleCarriers: boolean,
 *   isDdpEligibleBySellerLabelProviderId: (sellerLabelProviderId: string|number) => boolean,
 *   isWalletedDdpBySellerLabelProviderId: (sellerLabelProviderId: string|number) => boolean,
 *   ddpEligibleProviderIds: string[],
 *   ddpWalletedProviderIds: string[],
 * }}
 */
export const useDdpEligibleCarriers = () => {
  const labelProviders = useSelector(Global.selectSellerLabelProviders);
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry);

  // Get country-specific carrier configuration
  const ddpEligibleProviderIds = useMemo(() => {
    return getDdpCarriersForCountry(sellerHomeCountry);
  }, [sellerHomeCountry]);

  // Get walleted carrier IDs for this country (used for label blocking)
  const ddpWalletedProviderIds = useMemo(() => {
    return getDdpWalletedCarriersForCountry(sellerHomeCountry);
  }, [sellerHomeCountry]);

  // Build set of seller's DDP-eligible provider IDs
  const ddpEligibleSellerProviderIds = useMemo(() => {
    return buildSellerProviderIdSet(labelProviders, ddpEligibleProviderIds);
  }, [labelProviders, ddpEligibleProviderIds]);

  // Build set of seller's walleted DDP provider IDs
  const ddpWalletedSellerProviderIds = useMemo(() => {
    return buildSellerProviderIdSet(labelProviders, ddpWalletedProviderIds);
  }, [labelProviders, ddpWalletedProviderIds]);

  const hasDdpEligibleCarriers = ddpEligibleSellerProviderIds.size > 0;

  /**
   * Check if a seller label provider is DDP eligible
   * @param {string|number} sellerLabelProviderId
   * @returns {boolean}
   */
  const isDdpEligibleBySellerLabelProviderId = useCallback(
    (sellerLabelProviderId) => {
      return ddpEligibleSellerProviderIds.has(sellerLabelProviderId);
    },
    [ddpEligibleSellerProviderIds],
  );

  /**
   * Check if a seller label provider is a walleted DDP carrier
   * Used for determining "Guaranteed" vs "Estimated" duties and for label blocking
   * @param {string|number} sellerLabelProviderId
   * @returns {boolean}
   */
  const isWalletedDdpBySellerLabelProviderId = useCallback(
    (sellerLabelProviderId) => {
      return ddpWalletedSellerProviderIds.has(sellerLabelProviderId);
    },
    [ddpWalletedSellerProviderIds],
  );

  return {
    // Core eligibility
    hasDdpEligibleCarriers,
    isDdpEligibleBySellerLabelProviderId,

    // Walleted carriers (for label blocking and "Guaranteed" badge)
    isWalletedDdpBySellerLabelProviderId,

    // Raw provider ID arrays (for debugging/advanced use)
    ddpEligibleProviderIds,
    ddpWalletedProviderIds,
  };
};
